import {
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Grid,
  Box,
} from "@mui/material";
import { Formik, Form, Field } from "formik";
import React, { useState } from "react";
import { MarketLoadType } from "./types";
import * as Yup from "yup";
import { marketLoadApi } from "./apiService";
import { Loading } from "../../../components/Loading";
import { useToast } from "../../../components/Toast";
import { commonTextFieldProps } from "../../../utils/styles";

interface MarketLoadActionModalProps {
  open: boolean;
  onClose: () => void;
  marketLoad: MarketLoadType | null;
  isEdit?: boolean;
  onSuccess?: () => void;
}

const validationSchema = Yup.object().shape({
  vehicleNumber: Yup.string().required("Vehicle number is required"),
  vehicleCompanyName: Yup.string().optional(),
  ownerName: Yup.string().required("Owner name is required"),
  ownerContactNumber: Yup.string()
    .required("Owner contact number is required")
    .matches(/^\d{10}$/, "Contact number must be 10 digits"),
  companyName: Yup.string().optional(),
  primaryContactName: Yup.string().required("Primary contact name is required"),
  primaryContactNumber: Yup.string()
    .required("Primary contact number is required")
    .matches(/^\d{10}$/, "Contact number must be 10 digits"),
  gstNumber: Yup.string()
    .matches(
      /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
      "Invalid GST number format"
    )
    .optional(),
});

const MarketLoadActionModal = ({
  open,
  onClose,
  marketLoad,
  isEdit = false,
  onSuccess,
}: MarketLoadActionModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();
  const localUser = sessionStorage.getItem("user");
  const companyId = localUser ? JSON.parse(localUser).companyId : null;

  const initialValues: Partial<MarketLoadType> = {
    id: null,
    vehicleNumber: "",
    vehicleCompanyName: "",
    ownerName: "",
    ownerContactNumber: "",
    companyName: "",
    primaryContactName: "",
    primaryContactNumber: "",
    gstNumber: "",
    companyId,
  };

  const handleSubmit = async (values: Partial<MarketLoadType>) => {
    try {
      setIsSubmitting(true);
      if (isEdit && marketLoad?.id) {
        await marketLoadApi.updateMarketLoad(marketLoad.id, values);
        showToast("Market load updated successfully", "success");
      } else {
        const { id, ...createValues } = values;
        await marketLoadApi.createMarketLoad(createValues);
        showToast("Market load created successfully", "success");
      }
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error("Error saving market load:", error);
      showToast("Failed to save market load", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting) {
    return <Loading message="Saving..." fullScreen={true} />;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontSize: "1rem" }}>
        {isEdit ? "Edit Market Load" : "Add New Market Load"}
      </DialogTitle>
      <DialogContent>
        <Formik
          initialValues={isEdit && marketLoad ? marketLoad : initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, handleChange, values }) => (
            <Form>
              <Box sx={{ mt: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="vehicleNumber"
                      label="Vehicle Number"
                      variant="outlined"
                      error={
                        touched.vehicleNumber && Boolean(errors.vehicleNumber)
                      }
                      helperText={touched.vehicleNumber && errors.vehicleNumber}
                      onChange={handleChange}
                      value={values.vehicleNumber}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="vehicleCompanyName"
                      label="Vehicle Company Name"
                      variant="outlined"
                      error={
                        touched.vehicleCompanyName &&
                        Boolean(errors.vehicleCompanyName)
                      }
                      helperText={
                        touched.vehicleCompanyName && errors.vehicleCompanyName
                      }
                      onChange={handleChange}
                      value={values.vehicleCompanyName}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="ownerName"
                      label="Owner Name"
                      variant="outlined"
                      error={touched.ownerName && Boolean(errors.ownerName)}
                      helperText={touched.ownerName && errors.ownerName}
                      onChange={handleChange}
                      value={values.ownerName}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="ownerContactNumber"
                      label="Owner Contact Number"
                      variant="outlined"
                      error={
                        touched.ownerContactNumber &&
                        Boolean(errors.ownerContactNumber)
                      }
                      helperText={
                        touched.ownerContactNumber && errors.ownerContactNumber
                      }
                      onChange={handleChange}
                      value={values.ownerContactNumber}
                      {...commonTextFieldProps}
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        maxLength: 10,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="companyName"
                      label="Company Name"
                      variant="outlined"
                      error={touched.companyName && Boolean(errors.companyName)}
                      helperText={touched.companyName && errors.companyName}
                      onChange={handleChange}
                      value={values.companyName}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="primaryContactName"
                      label="Primary Contact Name"
                      variant="outlined"
                      error={
                        touched.primaryContactName &&
                        Boolean(errors.primaryContactName)
                      }
                      helperText={
                        touched.primaryContactName && errors.primaryContactName
                      }
                      onChange={handleChange}
                      value={values.primaryContactName}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="primaryContactNumber"
                      label="Primary Contact Number"
                      variant="outlined"
                      error={
                        touched.primaryContactNumber &&
                        Boolean(errors.primaryContactNumber)
                      }
                      helperText={
                        touched.primaryContactNumber &&
                        errors.primaryContactNumber
                      }
                      onChange={handleChange}
                      value={values.primaryContactNumber}
                      {...commonTextFieldProps}
                      inputProps={{
                        inputMode: "numeric",
                        pattern: "[0-9]*",
                        maxLength: 10,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Field
                      as={TextField}
                      fullWidth
                      name="gstNumber"
                      label="GST Number"
                      variant="outlined"
                      error={touched.gstNumber && Boolean(errors.gstNumber)}
                      helperText={touched.gstNumber && errors.gstNumber}
                      onChange={handleChange}
                      value={values.gstNumber}
                      {...commonTextFieldProps}
                    />
                  </Grid>
                </Grid>
                <Box
                  sx={{
                    mt: 3,
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={onClose}
                    size="small"
                    sx={{
                      height: 30,
                      fontSize: "0.8rem",
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="small"
                    sx={{
                      height: 30,
                      fontSize: "0.8rem",
                    }}
                  >
                    {isEdit ? "Update" : "Save"}
                  </Button>
                </Box>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

export default MarketLoadActionModal;
